import { useCallback, useEffect, useState } from "react";
import {
  useGetVapidPublicKeyQuery,
  useSubscribePushMutation,
  useUnsubscribePushMutation,
} from "../services/rtkApi/messengerApi";

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export type PushServerReadiness =
  | "skipped"
  | "loading"
  | "configured"
  | "not_configured"
  | "unreachable";

export function useWebPush(enabled: boolean) {
  const {
    data: vapidRes,
    refetch: refetchVapid,
    isError: vapidFetchFailed,
    isFetching: vapidFetching,
    isLoading: vapidLoading,
  } = useGetVapidPublicKeyQuery(undefined, {
    skip: !enabled,
  });
  const [subscribePush, { isLoading: subscribing }] = useSubscribePushMutation();
  const [unsubscribePush, { isLoading: unsubscribing }] = useUnsubscribePushMutation();
  const [error, setError] = useState<string | null>(null);
  // per-browser subscription — do not use server subscriptionCount for toggle (multi-device)
  const [localSubscribed, setLocalSubscribed] = useState<boolean | null>(null);
  /** undefined = still probing pushManager; null = no local subscription; string = endpoint */
  const [localPushEndpoint, setLocalPushEndpoint] = useState<string | null | undefined>(undefined);

  const refreshLocalSubscription = useCallback(async () => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      setLocalSubscribed(false);
      setLocalPushEndpoint(null);
      return;
    }
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      setLocalSubscribed(!!sub);
      setLocalPushEndpoint(sub?.endpoint ?? null);
    } catch {
      setLocalSubscribed(false);
      setLocalPushEndpoint(null);
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      setLocalSubscribed(null);
      setLocalPushEndpoint(undefined);
      return;
    }
    void refreshLocalSubscription();
  }, [enabled, refreshLocalSubscription]);

  const serverConfigured = vapidRes?.data?.configured === true && !!vapidRes?.data?.publicKey;

  const pushServerReadiness: PushServerReadiness = !enabled
    ? "skipped"
    : vapidFetchFailed
      ? "unreachable"
      : vapidLoading || (vapidFetching && !vapidRes)
        ? "loading"
        : serverConfigured
          ? "configured"
          : "not_configured";

  const subscribe = useCallback(async () => {
    setError(null);
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      setError("This browser does not support web push.");
      return false;
    }
    const vapid = await refetchVapid();
    const publicKey = vapid.data?.data?.publicKey;
    if (!publicKey || !vapid.data?.data?.configured) {
      setError("Web push is not configured on the server.");
      return false;
    }
    const perm = await Notification.requestPermission();
    if (perm !== "granted") {
      setError(
        perm === "denied"
          ? "Notifications are blocked. Enable them in your browser settings for this site."
          : "Notification permission was not granted.",
      );
      return false;
    }
    const registration = await navigator.serviceWorker.register("/sw.js");
    await navigator.serviceWorker.ready;
    const sub = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
    const json = sub.toJSON();
    if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
      setError("Could not read push subscription from the browser.");
      return false;
    }
    const res = await subscribePush({
      endpoint: json.endpoint,
      keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    }).unwrap();
    if (!res.success) {
      setError(res.error || "Failed to save subscription.");
      return false;
    }
    await refreshLocalSubscription();
    return true;
  }, [refetchVapid, refreshLocalSubscription, subscribePush]);

  const unsubscribe = useCallback(async () => {
    setError(null);
    try {
      const registration = await navigator.serviceWorker.ready;
      const sub = await registration.pushManager.getSubscription();
      if (sub) {
        await unsubscribePush({ endpoint: sub.endpoint }).unwrap();
        await sub.unsubscribe();
      } else {
        setError("No push subscription on this browser to remove.");
        return false;
      }
      await refreshLocalSubscription();
      return true;
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      return false;
    }
  }, [refreshLocalSubscription, unsubscribePush]);

  return {
    serverConfigured,
    pushServerReadiness,
    localSubscribed,
    localPushEndpoint,
    refreshLocalSubscription,
    subscribing,
    unsubscribing,
    error,
    setError,
    subscribe,
    unsubscribe,
  };
}
